import {
  IPageConfig,
  IPageNavigation,
} from "@/@types/filoxenia-config/nav-bar";

export const HOME_MAIN = "home_main";
export const HOME_MIN = "home_min";
export const PALETTE = "palette";
export const HOME_ROUTE = "Home";
export const FOLDER_ROUTE = "StandartSubMenuPage";

export type NavTargetKey = "home_main" | "home_min" | "palette" | string;

export type NavPlacement =
  | { kind: "home_main" }
  | { kind: "home_min" }
  | { kind: "folder"; folderId: string }
  | { kind: "palette" };

export function isFolder(page: IPageConfig): boolean {
  return page.route === FOLDER_ROUTE;
}

export function isHome(page: IPageConfig): boolean {
  return page.route === HOME_ROUTE;
}

export function getHome(pages: Array<IPageConfig>): IPageConfig | undefined {
  return pages.find((page) => isHome(page));
}

export function getFolders(pages: Array<IPageConfig>): Array<IPageConfig> {
  return pages.filter((page) => isFolder(page));
}

export function normalizePage(page: IPageConfig): IPageConfig {
  const navigation: Partial<IPageNavigation> = page.navigation || {};
  return {
    ...page,
    grid: page.grid || 1,
    navigation: {
      children: navigation.children || [],
      pageGroup: navigation.pageGroup || "",
      navigationGroup: navigation.navigationGroup || page.nav || "",
    },
  };
}

export function placementOf(
  page: IPageConfig,
  homeId: string | undefined,
  folderIds: Array<string>
): NavPlacement {
  const pageGroup = page.navigation?.pageGroup || "";
  const navigationGroup = page.navigation?.navigationGroup || "";
  const legacyNav = page.nav || "";
  if (
    homeId &&
    (pageGroup === homeId ||
      (!pageGroup &&
        (navigationGroup === HOME_MAIN ||
          navigationGroup === HOME_MIN ||
          legacyNav === HOME_MAIN ||
          legacyNav === HOME_MIN)))
  ) {
    const group = navigationGroup || legacyNav;
    return group === HOME_MIN ? { kind: "home_min" } : { kind: "home_main" };
  }
  if (folderIds.includes(pageGroup)) {
    return { kind: "folder", folderId: pageGroup };
  }
  return { kind: "palette" };
}

export function sortedByOrder(pages: Array<IPageConfig>): Array<IPageConfig> {
  return [...pages].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
}

export function homeMainList(
  pages: Array<IPageConfig>,
  homeId: string | undefined
): Array<IPageConfig> {
  if (!homeId) return [];
  return sortedByOrder(
    pages.filter(
      (page) =>
        !isHome(page) && placementOf(page, homeId, []).kind === "home_main"
    )
  );
}

export function homeMinList(
  pages: Array<IPageConfig>,
  homeId: string | undefined
): Array<IPageConfig> {
  if (!homeId) return [];
  return sortedByOrder(
    pages.filter(
      (page) =>
        !isHome(page) && placementOf(page, homeId, []).kind === "home_min"
    )
  );
}

export function folderChildrenList(
  pages: Array<IPageConfig>,
  folderId: string
): Array<IPageConfig> {
  return sortedByOrder(
    pages.filter((page) => page.navigation?.pageGroup === folderId)
  );
}

export function paletteList(
  pages: Array<IPageConfig>,
  homeId: string | undefined,
  folderIds: Array<string>
): Array<IPageConfig> {
  return sortedByOrder(
    pages.filter(
      (page) =>
        !isHome(page) && placementOf(page, homeId, folderIds).kind === "palette"
    )
  );
}

function clonePage(page: IPageConfig): IPageConfig {
  return {
    ...page,
    navigation: {
      children: [],
      pageGroup: "",
      navigationGroup: "",
      ...(page.navigation || {}),
    },
  };
}

function setPlacement(
  page: IPageConfig,
  pageGroup: string,
  navigationGroup: string
): IPageConfig {
  const updated = clonePage(page);
  updated.nav = navigationGroup;
  updated.navigation = {
    children: [...(updated.navigation?.children || [])],
    pageGroup,
    navigationGroup,
  };
  return updated;
}

function insertAt(
  list: Array<IPageConfig>,
  pageId: string,
  newIndex: number
): Array<IPageConfig> {
  const page = list.find((item) => item._id === pageId);
  const others = list.filter((item) => item._id !== pageId);
  if (!page) return list;
  const index = Math.max(0, Math.min(newIndex, others.length));
  return [...others.slice(0, index), page, ...others.slice(index)];
}

function withChildren(
  pages: Array<IPageConfig>,
  parentId: string,
  childrenIds: Array<string>,
  updateLegacy: boolean
): Array<IPageConfig> {
  return pages.map((page) => {
    if (page._id !== parentId) return page;
    const updated = clonePage(page);
    if (updateLegacy) updated.children = [...childrenIds];
    updated.navigation = {
      ...(updated.navigation || {
        children: [],
        pageGroup: "",
        navigationGroup: "",
      }),
      children: [...childrenIds],
    };
    return updated;
  });
}

/**
 * Глобальная перенумерация sort_order и синхронизация списков детей
 * (home.navigation.children и folder.children/navigation.children).
 */
export function renumber(
  pages: Array<IPageConfig>,
  override?: {
    target: NavTargetKey;
    list: Array<IPageConfig>;
  }
): Array<IPageConfig> {
  const home = getHome(pages);
  const homeId = home ? home._id : undefined;
  const folders = sortedByOrder(getFolders(pages));

  let main = homeMainList(pages, homeId);
  let min = homeMinList(pages, homeId);
  let palette = paletteList(
    pages,
    homeId,
    folders.map((f) => f._id)
  );

  const childrenByFolder: Record<string, Array<IPageConfig>> = {};
  for (const folder of folders) {
    childrenByFolder[folder._id] = folderChildrenList(pages, folder._id);
  }

  if (override) {
    if (override.target === HOME_MAIN) main = override.list;
    else if (override.target === HOME_MIN) min = override.list;
    else if (override.target === PALETTE) palette = override.list;
    else if (childrenByFolder[override.target]) {
      childrenByFolder[override.target] = override.list;
    }
  }

  const order: Array<IPageConfig> = [
    ...main,
    ...min,
    ...folders.flatMap((folder) => [
      folder,
      ...(childrenByFolder[folder._id] || []),
    ]),
    ...palette,
  ];

  let next = pages.map((page) => clonePage(page));
  const seen: Record<string, boolean> = {};
  let counter = 0;
  for (const item of order) {
    if (seen[item._id]) continue;
    seen[item._id] = true;
    next = next.map((p) =>
      p._id === item._id ? { ...clonePage(p), sort_order: counter } : p
    );
    counter++;
  }

  if (home) {
    next = withChildren(
      next,
      home._id,
      [...main, ...min].map((item) => item._id),
      false
    );
  }
  for (const folder of folders) {
    next = withChildren(
      next,
      folder._id,
      (childrenByFolder[folder._id] || []).map((item) => item._id),
      true
    );
  }

  return next;
}

/**
 * Перемещает страницу в целевую группу, обновляет pageGroup/navigationGroup,
 * sort_order и children связанных страниц.
 */
export function applyMove(
  pages: Array<IPageConfig>,
  pageId: string,
  target: NavTargetKey,
  newIndex: number
): Array<IPageConfig> {
  const home = getHome(pages);
  const homeId = home ? home._id : undefined;
  const folderIds = getFolders(pages).map((folder) => folder._id);

  const page = pages.find((item) => item._id === pageId);
  if (!page) return pages;

  let next = pages.map((page) => clonePage(page));

  if (target === HOME_MAIN && homeId) {
    next = next.map((item) =>
      item._id === pageId ? setPlacement(item, homeId, HOME_MAIN) : item
    );
  } else if (target === HOME_MIN && homeId) {
    next = next.map((item) =>
      item._id === pageId ? setPlacement(item, homeId, HOME_MIN) : item
    );
  } else if (target === PALETTE) {
    next = next.map((item) =>
      item._id === pageId ? setPlacement(item, "", "") : item
    );
  } else if (folderIds.includes(target)) {
    next = next.map((item) =>
      item._id === pageId ? setPlacement(item, target, "") : item
    );
  } else {
    return pages;
  }

  let targetList: Array<IPageConfig>;
  if (target === HOME_MAIN) {
    targetList = insertAt(homeMainList(next, homeId), pageId, newIndex);
  } else if (target === HOME_MIN) {
    targetList = insertAt(homeMinList(next, homeId), pageId, newIndex);
  } else if (target === PALETTE) {
    targetList = insertAt(
      paletteList(next, homeId, folderIds),
      pageId,
      newIndex
    );
  } else {
    targetList = insertAt(folderChildrenList(next, target), pageId, newIndex);
  }

  return renumber(next, { target, list: targetList });
}

/** Удаляет страницу, чистит ссылки у родителей и перенумеровывает. */
export function applyDelete(
  pages: Array<IPageConfig>,
  pageId: string
): Array<IPageConfig> {
  const next = pages
    .filter((page) => page._id !== pageId)
    .map((page) => clonePage(page));
  return renumber(next);
}

export interface INavPageUpdate {
  _id: string;
  [key: string]: unknown;
}

export function diffUpdates(
  before: Array<IPageConfig>,
  after: Array<IPageConfig>
): Array<INavPageUpdate> {
  const updates: Array<INavPageUpdate> = [];
  for (const oldPage of before) {
    const newPage = after.find((item) => item._id === oldPage._id);
    if (!newPage) continue;
    const patch: INavPageUpdate = { _id: oldPage._id };
    if (newPage.sort_order !== oldPage.sort_order) {
      patch.sort_order = newPage.sort_order;
    }
    if ((newPage.nav || "") !== (oldPage.nav || "")) {
      patch.nav = newPage.nav || "";
    }
    if (
      JSON.stringify(newPage.navigation) !== JSON.stringify(oldPage.navigation)
    ) {
      patch.navigation = newPage.navigation;
    }
    if (
      JSON.stringify(newPage.children || []) !==
      JSON.stringify(oldPage.children || [])
    ) {
      patch.children = newPage.children || [];
    }
    if (Object.keys(patch).length > 1) {
      updates.push(patch);
    }
  }
  return updates;
}
